import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Loader2, Save, Eye, Upload, AlertCircle } from 'lucide-react';
import { ImageGalleryManager, GalleryImage } from '@/components/admin/ImageGalleryManager';

interface AreaData {
  id: string;
  name: string;
  slug: string;
  town_id: string;
  intro_summary: string | null;
  about_area: string | null;
  hero_image_url: string | null;
  hero_image_attribution: string | null;
  gallery_images: GalleryImage[] | null;
  highlights_json: any;
  faqs_json: any;
  zip_codes: string[] | null;
  is_published: boolean;
  content_state: string | null;
  draft_content: any | null;
  published_content: any | null;
}

interface TownData {
  name: string;
  slug: string;
  state: string | null;
}

export default function AreaContentEdit() {
  const { areaId } = useParams<{ areaId: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [area, setArea] = useState<AreaData | null>(null);
  const [town, setTown] = useState<TownData | null>(null);
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  
  // Form state
  const [areaName, setAreaName] = useState('');
  const [introSummary, setIntroSummary] = useState('');
  const [aboutArea, setAboutArea] = useState('');
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [zipCodes, setZipCodes] = useState('');
  const [isPublished, setIsPublished] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }

    if (user && areaId) {
      fetchArea();
    }
  }, [user, authLoading, areaId, navigate]);

  const fetchArea = async () => {
    try {
      const { data, error } = await supabase
        .from('areas')
        .select('id, name, slug, town_id, intro_summary, about_area, hero_image_url, hero_image_attribution, gallery_images, highlights_json, faqs_json, zip_codes, is_published, content_state, draft_content, published_content')
        .eq('id', areaId)
        .single();

      if (error) throw error;

      // Cast JSONB fields properly
      const areaData: AreaData = {
        ...data,
        gallery_images: Array.isArray(data.gallery_images)
          ? (data.gallery_images as unknown as GalleryImage[])
          : null,
        content_state: data.content_state,
        draft_content: data.draft_content,
        published_content: data.published_content,
      };

      // Check for unpublished changes
      const draftJson = JSON.stringify({
        intro_summary: data.intro_summary,
        about_area: data.about_area,
        gallery_images: data.gallery_images,
      });
      const publishedJson = data.published_content ? JSON.stringify(data.published_content) : null;
      setHasUnpublishedChanges(!!publishedJson && draftJson !== publishedJson);

      setArea(areaData);
      setAreaName(areaData.name);
      setIntroSummary(areaData.intro_summary || '');
      setAboutArea(areaData.about_area || '');
      
      // Convert existing hero image + gallery to unified gallery
      const existingGallery: GalleryImage[] = areaData.gallery_images || [];
      if (areaData.hero_image_url && existingGallery.length === 0) {
        // Migrate existing hero image to gallery
        setGalleryImages([{ url: areaData.hero_image_url, attribution: areaData.hero_image_attribution || '' }]);
      } else {
        setGalleryImages(existingGallery);
      }
      
      setZipCodes(areaData.zip_codes?.join(', ') || '');
      setIsPublished(areaData.is_published);

      // Fetch town info
      const { data: townData } = await supabase
        .from('towns')
        .select('name, slug, state')
        .eq('id', data.town_id)
        .single();

      if (townData) {
        setTown(townData);
      }
    } catch (error) {
      console.error('Error fetching area:', error);
      toast({
        title: 'Error loading area',
        description: 'Could not load area data',
        variant: 'destructive',
      });
      navigate('/admin/areas');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!area) return;

    // Track if area is being published (was unpublished, now published)
    const areaBeingPublished = !area.is_published && isPublished;

    setSaving(true);
    try {
      // Parse zip codes
      const zipCodeArray = zipCodes
        .split(',')
        .map(z => z.trim())
        .filter(z => z.length > 0);

      // First image becomes the hero image for backward compatibility
      const heroImage = galleryImages[0] || null;

      // Generate slug from name
      const slug = areaName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();

      const { error } = await supabase
        .from('areas')
        .update({
          name: areaName,
          slug: slug,
          intro_summary: introSummary || null,
          about_area: aboutArea || null,
          hero_image_url: heroImage?.url || null,
          hero_image_attribution: heroImage?.attribution || null,
          gallery_images: galleryImages as unknown as any,
          zip_codes: zipCodeArray.length > 0 ? zipCodeArray : null,
          is_published: isPublished,
          content_state: 'draft',
        })
        .eq('id', area.id);

      if (error) throw error;

      // Update local area state
      setArea(prev => prev ? { ...prev, is_published: isPublished } : null);
      setHasUnpublishedChanges(true);

      // Update local area state
      setArea(prev => prev ? { ...prev, is_published: isPublished } : null);

      // Auto-trigger IndexNow when area is published
      if (areaBeingPublished) {
        // Get site ID from area's town
        const { data: areaData } = await supabase
          .from('areas')
          .select('site_id')
          .eq('id', area.id)
          .single();

        if (areaData?.site_id) {
          supabase.functions.invoke('auto-indexnow', {
            body: { siteId: areaData.site_id, trigger: 'area_published', entityId: area.id }
          }).then(({ error }) => {
            if (error) {
              console.error('Auto-IndexNow error:', error);
            } else {
              console.log('Auto-IndexNow triggered for area publish');
            }
          });
        }
      }

      toast({
        title: 'Content saved!',
        description: 'Your changes have been saved successfully.',
      });
    } catch (error: any) {
      console.error('Error saving:', error);
      toast({
        title: 'Error saving',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!area) return;

    setPublishing(true);
    try {
      // First save current changes
      await handleSave();

      // Create published content snapshot
      const publishedContent = {
        intro_summary: introSummary,
        about_area: aboutArea,
        gallery_images: galleryImages,
        zip_codes: zipCodes.split(',').map(z => z.trim()).filter(z => z),
      };

      const { error } = await supabase
        .from('areas')
        .update({
          published_content: publishedContent as unknown as any,
          content_state: 'published',
        })
        .eq('id', area.id);

      if (error) throw error;

      setHasUnpublishedChanges(false);
      toast({
        title: 'Published!',
        description: 'Area content is now live on your site.',
      });
    } catch (error: any) {
      console.error('Error publishing:', error);
      toast({
        title: 'Error publishing',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setPublishing(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !area) return null;

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-background border-b border-border sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin/areas')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <span className="font-display text-xl">Edit Area</span>
                <span className="text-muted-foreground ml-2">— {area.name}</span>
                {town && (
                  <span className="text-muted-foreground text-sm ml-2">in {town.name}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasUnpublishedChanges && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Unpublished Changes
                </Badge>
              )}
              {!hasUnpublishedChanges && area.published_content && (
                <Badge variant="default">Published</Badge>
              )}
              <Button 
                variant="outline" 
                onClick={() => window.open(`/${town?.slug}/${area.slug}?preview=true`, '_blank')}
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
              {hasUnpublishedChanges && (
                <Button variant="default" onClick={handlePublish} disabled={publishing}>
                  {publishing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Publish
                </Button>
              )}
              <Button variant="gold" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Draft
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Area Name & Publish Toggle */}
        <Card>
          <CardHeader>
            <CardTitle>Area Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="areaName">Area Name</Label>
              <Input
                id="areaName"
                value={areaName}
                onChange={(e) => setAreaName(e.target.value)}
                placeholder="Enter area name"
              />
            </div>
            <div className="flex items-center justify-between pt-4 border-t">
              <div>
                <h3 className="font-medium">Publish Status</h3>
                <p className="text-sm text-muted-foreground">
                  {isPublished ? 'This area is live and visible to visitors' : 'This area is in draft mode'}
                </p>
              </div>
              <Switch
                checked={isPublished}
                onCheckedChange={setIsPublished}
              />
            </div>
          </CardContent>
        </Card>

        {/* Hero Section */}
        <Card>
          <CardHeader>
            <CardTitle>Hero Section</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Image Gallery */}
            <ImageGalleryManager
              images={galleryImages}
              onChange={setGalleryImages}
              storagePath={`areas/${area.id}`}
              label="Area Images"
            />
          </CardContent>
        </Card>

        {/* About Section */}
        <Card>
          <CardHeader>
            <CardTitle>About {area.name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="introSummary">Intro Summary (short)</Label>
              <Textarea
                id="introSummary"
                value={introSummary}
                onChange={(e) => setIntroSummary(e.target.value)}
                placeholder="A brief one-line summary of this neighborhood..."
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="aboutArea">About (HTML allowed)</Label>
              <Textarea
                id="aboutArea"
                value={aboutArea}
                onChange={(e) => setAboutArea(e.target.value)}
                placeholder="<p>Detailed description of this neighborhood...</p>"
                rows={8}
                className="font-mono text-sm"
              />
            </div>
          </CardContent>
        </Card>

        {/* Zip Codes */}
        <Card>
          <CardHeader>
            <CardTitle>Location Data</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="zipCodes">Zip Codes (comma separated)</Label>
              <Input
                id="zipCodes"
                value={zipCodes}
                onChange={(e) => setZipCodes(e.target.value)}
                placeholder="02738, 02739"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter zip codes that cover this area, separated by commas
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
